export type UiFailureClass = 'network' | 'backend_degraded' | 'query_timeout' | 'auth' | 'other';

type UiHealthEvent = {
  ts: number; // epoch ms
  kind: 'ok' | 'error' | 'auth_fallback';
  endpoint?: string;
  method?: string;
  status?: number;
  failureClass?: UiFailureClass;
};

/** Classify API failures so query/backend timeouts are not labeled as pure network loss. */
export function classifyUiFailure(status: number | undefined, message: string | undefined): UiFailureClass {
  const msg = String(message || '').toLowerCase();
  if (status === 401 || status === 403) return 'auth';
  if (
    status === 408
    || msg.includes('timeout')
    || msg.includes('econnaborted')
    || msg.includes('timed out')
  ) {
    return 'query_timeout';
  }
  if (
    status === 503
    || status === 429
    || status === 502
    || status === 504
    || msg.includes('db_capacity_exhausted')
    || msg.includes('backend_degraded')
    || msg.includes('pool_acquire')
  ) {
    return 'backend_degraded';
  }
  if (status == null || status === 0) return 'network';
  if (status >= 500) return 'backend_degraded';
  return 'other';
}

export type UiHealthSnapshot = {
  now_ts: number;
  // last known good fetch times (epoch ms)
  last_ok_any_ts: number | null;
  last_ok_incidents_list_ts: number | null;
  last_ok_incident_detail_ts: number | null;
  last_ok_edge_health_stages_ts: number | null;
  // counters within a window
  window_sec: number;
  counts: {
    ok: number;
    error: number;
    auth_fallback: number;
    http_401: number;
    http_403: number;
  };
  last_error?: {
    ts: number;
    endpoint?: string;
    status?: number;
    message?: string;
    failure_class?: UiFailureClass;
  } | null;
  failure_counts?: {
    network: number;
    backend_degraded: number;
    query_timeout: number;
    auth: number;
    other: number;
  };
};

const WINDOW_SEC = 15 * 60; // 15m
const MAX_EVENTS = 500;

const _state: {
  events: UiHealthEvent[];
  lastOkAnyTs: number | null;
  lastOkIncidentsListTs: number | null;
  lastOkIncidentDetailTs: number | null;
  lastOkEdgeHealthStagesTs: number | null;
  lastError: UiHealthSnapshot['last_error'];
} = {
  events: [],
  lastOkAnyTs: null,
  lastOkIncidentsListTs: null,
  lastOkIncidentDetailTs: null,
  lastOkEdgeHealthStagesTs: null,
  lastError: null,
};

function _now(): number {
  return Date.now();
}

function _trimEvents(): void {
  const cutoff = _now() - WINDOW_SEC * 1000;
  _state.events = _state.events.filter((e) => e.ts >= cutoff);
  if (_state.events.length > MAX_EVENTS) {
    _state.events = _state.events.slice(_state.events.length - MAX_EVENTS);
  }
}

function _classifyEndpoint(url: string | undefined | null): 'incidents_list' | 'incident_detail' | 'edge_health_stages' | 'other' {
  const u = String(url || '');
  if (!u) return 'other';
  // Dashboard uses same-origin baseURL; config.url may be relative.
  if (u.includes('/api/v1/incidents')) {
    // heuristic: detail fetch usually includes an incident id segment after /incidents/
    const parts = u.split('/api/v1/incidents', 2);
    const tail = parts.length > 1 ? parts[1] : '';
    if (tail && tail.length > 2 && tail.includes('/')) return 'incident_detail';
    return 'incidents_list';
  }
  if (u.includes('/edge/health/stages')) return 'edge_health_stages';
  return 'other';
}

export function uiHealthOnAuthFallback(): void {
  _state.events.push({ ts: _now(), kind: 'auth_fallback' });
  _trimEvents();
}

export function uiHealthOnResponse(configUrl: string | undefined, method: string | undefined): void {
  const ts = _now();
  _state.events.push({ ts, kind: 'ok', endpoint: configUrl, method });
  _state.lastOkAnyTs = ts;

  const cls = _classifyEndpoint(configUrl);
  if (cls === 'incidents_list') _state.lastOkIncidentsListTs = ts;
  if (cls === 'incident_detail') _state.lastOkIncidentDetailTs = ts;
  if (cls === 'edge_health_stages') _state.lastOkEdgeHealthStagesTs = ts;

  _trimEvents();
}

export function uiHealthOnError(configUrl: string | undefined, method: string | undefined, status: number | undefined, message: string | undefined): void {
  const ts = _now();
  const failureClass = classifyUiFailure(status, message);
  _state.events.push({ ts, kind: 'error', endpoint: configUrl, method, status, failureClass });
  _state.lastError = {
    ts,
    endpoint: configUrl,
    status,
    message: String(message || '').slice(0, 300) || undefined,
    failure_class: failureClass,
  };
  _trimEvents();
}

export function uiHealthSnapshot(): UiHealthSnapshot {
  _trimEvents();
  const counts = {
    ok: 0,
    error: 0,
    auth_fallback: 0,
    http_401: 0,
    http_403: 0,
  };
  const failure_counts = {
    network: 0,
    backend_degraded: 0,
    query_timeout: 0,
    auth: 0,
    other: 0,
  };
  for (const e of _state.events) {
    if (e.kind === 'ok') counts.ok += 1;
    if (e.kind === 'error') counts.error += 1;
    if (e.kind === 'auth_fallback') counts.auth_fallback += 1;
    if (e.kind === 'error' && e.status === 401) counts.http_401 += 1;
    if (e.kind === 'error' && e.status === 403) counts.http_403 += 1;
    if (e.kind === 'error') {
      const cls = e.failureClass || classifyUiFailure(e.status, undefined);
      failure_counts[cls] += 1;
    }
  }
  return {
    now_ts: _now(),
    last_ok_any_ts: _state.lastOkAnyTs,
    last_ok_incidents_list_ts: _state.lastOkIncidentsListTs,
    last_ok_incident_detail_ts: _state.lastOkIncidentDetailTs,
    last_ok_edge_health_stages_ts: _state.lastOkEdgeHealthStagesTs,
    window_sec: WINDOW_SEC,
    counts,
    failure_counts,
    last_error: _state.lastError || null,
  };
}

export function uiHealthStatus(): { status: 'ok' | 'degraded' | 'down' | 'unknown'; detail: any } {
  const snap = uiHealthSnapshot();
  const now = snap.now_ts;
  const okTs = snap.last_ok_any_ts;
  const ageSec = okTs ? (now - okTs) / 1000 : null;

  // If we cannot observe anything yet, it's unknown (fresh page load).
  if (!okTs) return { status: 'unknown', detail: { ...snap, last_ok_age_sec: null } };

  // Auth fallback indicates degraded (we had to recover from a stale JWT).
  const hadAuthFallback = snap.counts.auth_fallback > 0 || snap.counts.http_403 > 0;

  // Degraded posture: distinguish network loss from query/backend degradation (EPIC290).
  // 4xx (other than auth) usually indicate request validation / business rules, not connectivity.
  const failureCounts = snap.failure_counts || {
    network: 0,
    backend_degraded: 0,
    query_timeout: 0,
    auth: 0,
    other: 0,
  };
  const networkErrors = failureCounts.network;
  const backendErrors = failureCounts.backend_degraded + failureCounts.query_timeout;
  const connectivityErrors = networkErrors + backendErrors;

  // Basic thresholds; keep them generous to avoid noise.
  if (ageSec !== null && ageSec > 180) {
    return {
      status: 'down',
      detail: {
        ...snap,
        last_ok_age_sec: ageSec,
        hadAuthFallback,
        primary_failure_class: networkErrors > backendErrors ? 'network' : 'backend_degraded',
      },
    };
  }
  if (hadAuthFallback || connectivityErrors > 0) {
    const primary: UiFailureClass = backendErrors > 0 && backendErrors >= networkErrors
      ? (failureCounts.query_timeout >= failureCounts.backend_degraded ? 'query_timeout' : 'backend_degraded')
      : networkErrors > 0
        ? 'network'
        : 'other';
    return {
      status: 'degraded',
      detail: {
        ...snap,
        last_ok_age_sec: ageSec,
        hadAuthFallback,
        connectivityErrors,
        networkErrors,
        backendErrors,
        primary_failure_class: primary,
      },
    };
  }
  return { status: 'ok', detail: { ...snap, last_ok_age_sec: ageSec, hadAuthFallback } };
}

